import {
    Modal,
    ModalOverlay,
    ModalHeader,
    ModalBody,
    Text,
    Flex,
    ModalContent,
    ModalFooter,
    Box,
    Radio,
    RadioGroup,
    useToast,
    useMediaQuery,
    Select
} from '@chakra-ui/react';
import React, { useMemo, useState, useCallback } from 'react';
import ZaraButton from '../custom-components/buttons/Button';
import { useMutation, useQuery } from '@apollo/client';
import { useError } from '../../error-provider/ErrorProvider';
import { GetMe } from '../../graphql/queries/GetMe';
import { ChargePaymentMethod, AddMinutesQl } from '../../graphql/mutations/ChargePaymentMethod';
import AddPaymentModal from '../../pages/create-agent/subscription-plan/AddPaymentModal';
import useServerApi from '../../hooks/useServerApi';

const AddMinutes = ({ costPerMinute, isOpen, onClose, isTrial }) => {
    const [radioValue, setRadioValue] = useState('600');
    const [isMobile] = useMediaQuery('(max-width: 480px)');
    const toast = useToast();
    const { showError } = useError;
    const [onChargePaymentMethod, { loading: paymentMethodLoading }] = useMutation(ChargePaymentMethod, {
        onError(error) {
            console.log(error);
        },
    });
    const [onAddMinutes, { loading: addMinutesLoading }] = useMutation(AddMinutesQl, {
        onError(error) {
            console.log(error);
        },
    });
    const loading = paymentMethodLoading || addMinutesLoading;
    useQuery(GetMe, {
        onError(error) {
            showError(undefined, error);
        },
    });
    // Payment sources state and fetch logic
    const [{ get }] = useServerApi();
    const [paymentSources, setPaymentSources] = useState([]);
    const [selectedSource, setSelectedSource] = useState('');
    const [isAddSourceOpen, setIsAddSourceOpen] = useState(false);
    const setErrors = useState('')[1];

    const fetchPaymentSources = useCallback(async () => {
        const [paymentSuccess, paymentResponse] = await get('/rest_endpoints/stripe/crud/payment_sources');
        if (paymentSuccess) {
            const paymentSources = paymentResponse.payment_sources ?? [];
            setPaymentSources(paymentSources);
            setSelectedSource(paymentSources.length > 0 && paymentSources[0] ? paymentSources[0].payment_id : '');
        }
    }, [get]);

    React.useEffect(() => {
        fetchPaymentSources();
    }, [fetchPaymentSources]);

    const priceAmount = useMemo(() => {
        const fallbackCostPerMinute = 0.48; // Default cost per minute if we dont have a specific one
        const effectiveCostPerMinute = costPerMinute && costPerMinute > 0 ? costPerMinute : fallbackCostPerMinute;
        return (effectiveCostPerMinute * Number(radioValue)).toFixed(2);
    }, [radioValue, costPerMinute]);
    const paymentMethod = paymentSources && paymentSources.length > 0
        ? paymentSources.find((pm) => pm.payment_id === selectedSource) || paymentSources[0]
        : null;

    async function onSubmit() {
        try {
            let res = await onChargePaymentMethod({
                variables: {
                    paymentMethodId: paymentMethod?.payment_id,
                    amount: Number(priceAmount),
                },
            });
            let intentId = '';
            res.data.chargePaymentMethod.successVars.forEach((element) => {
                if (element.key === 'id') {
                    intentId = element.value;
                }
            });
            res = await onAddMinutes({
                variables: {
                    minutes: Number(radioValue),
                    paymentIntentId: intentId,
                },
            });
            toast({
                title: "You've successfully added minutes to your account",
                status: 'success',
                isClosable: true,
            });
            onClose();
        } catch (error) {
            console.log(error);
            toast({
                title: 'Failed to charge your card',
                status: 'error',
                isClosable: true,
            });
        }
        return;
    }

    return (
        <>
            <Modal isCentered isOpen={isOpen} onClose={onClose} motionPreset='slideInBottom'>
                <ModalOverlay />
                <ModalContent width="90%" maxW="600px">
                    <ModalHeader fontSize='18px' lineHeight='28px' fontWeight='700' textAlign='left'>
                        Buy Additional Minutes
                    </ModalHeader>
                    <ModalBody>
                        <Box mb='34px'></Box>
                        <Flex flexDir='column' alignItems='center' mb='46px'>
                            <Flex h='26px' w='100%' maxW='504px' alignItems='center' justifyContent='center' border='1px solid #E2E8F0'>
                                <Flex
                                    w='95%'
                                    h='4px'
                                    justifyContent='space-between'
                                    alignItems='center'
                                    background='#3182CE'
                                >
                                    <RadioGroup w='100%' h='16px' onChange={setRadioValue} value={radioValue}>
                                        <Flex justifyContent='space-between'>
                                            <Radio bg='white' value='600' />
                                            <Radio bg='white' value='1200' />
                                            <Radio bg='white' value='2400' />
                                        </Flex>
                                    </RadioGroup>
                                </Flex>
                            </Flex>
                            <Flex justifyContent='space-between' w='100%' maxW='504px'>
                                <Text color='#2D3748' fontSize={isMobile ? '14px' : '18px'}>
                                    600
                                </Text>
                                <Text color='#2D3748' fontSize={isMobile ? '14px' : '18px'}>
                                    1200
                                </Text>
                                <Text color='#2D3748' fontSize={isMobile ? '14px' : '18px'}>
                                    2400
                                </Text>
                            </Flex>
                        </Flex>
                        <Text>
                            Total value: <strong>$ {priceAmount}</strong>
                        </Text>
                        {/* Payment source dropdown */}
                        <Box mt={4} mb={2}>
                            {paymentSources && paymentSources.length > 0 ? (
                                <>
                                    <Text mb={1}>Select a payment source:</Text>
                                    <Select
                                        value={selectedSource}
                                        onChange={e => setSelectedSource(e.target.value)}
                                        style={{ padding: '8px', borderRadius: '4px', border: '1px solid #E2E8F0', minWidth: 200 }}
                                    >
                                        {paymentSources.map(source => (
                                            <option key={source.payment_id} value={source.payment_id}>
                                                **** **** **** {source.last4}
                                            </option>
                                        ))}
                                    </Select>
                                </>
                            ) : (
                                <Text color='red.500'>No payment sources found.</Text>
                            )}
                            <Box mt={2}>
                                <Text as='span' color='blue.500' cursor='pointer' onClick={() => setIsAddSourceOpen(true)}>
                                    Add New Card
                                </Text>
                            </Box>
                        </Box>
                    </ModalBody>
                    <ModalFooter p={isMobile ? '12px 16px' : '16px 24px'}>
                        <Flex alignItems='center' justifyContent={isMobile ? 'center' : 'flex-end'} w='100%' flexDir={isMobile ? 'column' : 'row'}>
                            <ZaraButton onClick={onClose} mr={isMobile ? '0' : '12px'} mb={isMobile ? '8px' : '0'} variant='secondary' width={isMobile ? '100%' : 'auto'}>
                                Cancel
                            </ZaraButton>
                            <ZaraButton isLoading={loading} isDisabled={loading || !paymentMethod} onClick={onSubmit} width={isMobile ? '100%' : 'auto'}>
                                Purchase
                            </ZaraButton>
                        </Flex>
                    </ModalFooter>
                </ModalContent>
            </Modal>
            {/* Add Payment Modal */}
            <AddPaymentModal
                isOpen={isAddSourceOpen}
                onClose={() => setIsAddSourceOpen(false)}
                onSuccess={async () => {
                    await fetchPaymentSources();
                    setIsAddSourceOpen(false);
                }}
                setErrors={setErrors}
            />
        </>
    );
};

export default AddMinutes;
